// Package imports:
import React, { useState } from 'react';
// Component imports:
import Table from 'react-bootstrap/Table';
// Service imports:
import { deleteOrderFromBook, formatNumber, insertOrderForBook } from '../../services/utils';
// Type imports:
import { PublicOrderTickData } from '../../types/api/PublicTypes';
import { WithBook } from '../../types/api/Types';

interface IOwnProps {
    sortedPublicOrderTickData: Array<WithBook<PublicOrderTickData>>;
    highlightOrderIds?: string[];
}

const OrderBookSnapshot: React.FC<IOwnProps> = ({ sortedPublicOrderTickData, highlightOrderIds }) => {
    const [hoveredBookEntry, setHoveredBookEntry] = useState<PublicOrderTickData | null>(null);

    // Replay all order operations in the given (pre-sliced) data to reconstruct the book.
    let bidOrdersInPriorityOrder: PublicOrderTickData[] = [];
    let askOrdersInPriorityOrder: PublicOrderTickData[] = [];

    for (const bookTickDataEntry of sortedPublicOrderTickData) {
        const { tick } = bookTickDataEntry;
        const { side } = tick;
        if (tick.update_code === 'Insert') {
            if (side === 'bid') {
                bidOrdersInPriorityOrder = insertOrderForBook(tick, bidOrdersInPriorityOrder, true);
            } else {
                askOrdersInPriorityOrder = insertOrderForBook(tick, askOrdersInPriorityOrder, false);
            }
        } else if (tick.update_code === 'Update') {
            if (side === 'bid') {
                bidOrdersInPriorityOrder = deleteOrderFromBook(tick, bidOrdersInPriorityOrder);
                bidOrdersInPriorityOrder = insertOrderForBook(tick, bidOrdersInPriorityOrder, true);
            } else {
                askOrdersInPriorityOrder = deleteOrderFromBook(tick, askOrdersInPriorityOrder);
                askOrdersInPriorityOrder = insertOrderForBook(tick, askOrdersInPriorityOrder, false);
            }
        } else {
            if (side === 'bid') {
                bidOrdersInPriorityOrder = deleteOrderFromBook(tick, bidOrdersInPriorityOrder);
            } else {
                askOrdersInPriorityOrder = deleteOrderFromBook(tick, askOrdersInPriorityOrder);
            }
        }
    }

    const sizeOfBiggerArray = Math.max(bidOrdersInPriorityOrder.length, askOrdersInPriorityOrder.length);

    const getRows = () => {
        if (sizeOfBiggerArray === 0) {
            return (
                <tr className='NoClick'>
                    <td className='text-left EmptyTableText' colSpan={4}>
                        Empty book
                    </td>
                </tr>
            );
        }

        const jsxRows: JSX.Element[] = [];
        for (let i = 0; i < sizeOfBiggerArray; i++) {
            const bidOrder: PublicOrderTickData | undefined = bidOrdersInPriorityOrder[i];
            const askOrder: PublicOrderTickData | undefined = askOrdersInPriorityOrder[i];

            const isHighlightedBid = !!(highlightOrderIds && bidOrder?.order_id != null && highlightOrderIds.includes(String(bidOrder.order_id)));
            const isHighlightedAsk = !!(highlightOrderIds && askOrder?.order_id != null && highlightOrderIds.includes(String(askOrder.order_id)));

            const bidTdClassName = isHighlightedBid
                ? (hoveredBookEntry === bidOrder ? 'CurrentHover' : 'Current')
                : (hoveredBookEntry === bidOrder ? 'Hover' : undefined);
            const askTdClassName = isHighlightedAsk
                ? (hoveredBookEntry === askOrder ? 'CurrentHover' : 'Current')
                : (hoveredBookEntry === askOrder ? 'Hover' : undefined);

            jsxRows.push(
                <tr key={i}>
                    <td
                        className={bidTdClassName}
                        onMouseEnter={() => setHoveredBookEntry(bidOrder || null)}
                        onMouseLeave={() => setHoveredBookEntry(null)}
                    >
                        {bidOrder ? formatNumber(bidOrder.quantity, 'volume') : null}
                    </td>
                    <td
                        className={bidTdClassName}
                        onMouseEnter={() => setHoveredBookEntry(bidOrder || null)}
                        onMouseLeave={() => setHoveredBookEntry(null)}
                    >
                        {bidOrder ? formatNumber(bidOrder.price) : null}
                    </td>
                    <td
                        className={askTdClassName}
                        onMouseEnter={() => setHoveredBookEntry(askOrder || null)}
                        onMouseLeave={() => setHoveredBookEntry(null)}
                    >
                        {askOrder ? formatNumber(askOrder.price) : null}
                    </td>
                    <td
                        className={askTdClassName}
                        onMouseEnter={() => setHoveredBookEntry(askOrder || null)}
                        onMouseLeave={() => setHoveredBookEntry(null)}
                    >
                        {askOrder ? formatNumber(askOrder.quantity, 'volume') : null}
                    </td>
                </tr>
            );
        }
        return jsxRows;
    };

    return (
        <div className='OrderBookSnapshot'>
            <Table className='CompressedTable NotHover'>
                <thead>
                    <tr>
                        <th scope='col'>Qty.</th>
                        <th scope='col'>Bid</th>
                        <th scope='col'>Ask</th>
                        <th scope='col'>Qty.</th>
                    </tr>
                </thead>
                <tbody>
                    {getRows()}
                </tbody>
            </Table>
        </div>
    );
};

export default OrderBookSnapshot;
